// src/Auth/ProfileProvider.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { abpnClient } from "../Services/AbpnClient";
import { PreCertUser } from "../Shared/Models/PreCertUser";
import { deepCopy } from "../Shared/Utility/DeepCopy";
import { IsErrorPage } from "../Shared/Utility/PreCertLocationHelper";
import { AuthContext } from "./AuthProvider";

interface IProfileContext {
    user: PreCertUser | null;
    isLoadingUser: boolean;
    updateSelectedTrainingProgramId: (id: number) => void;
}

export const ProfileContext = React.createContext<IProfileContext>(null as any);

function redirectOnce(url: string, key: string, cooldownMs = 3000) {
    try {
        const now = Date.now();
        const last = Number(sessionStorage.getItem(key) || "0");
        if (now - last < cooldownMs) return;
        sessionStorage.setItem(key, String(now));
    } catch { }
    window.location.replace(url);
}

const BASENAME = "/precert";
const ERROR_PATH = `${BASENAME}/errorPage`;

type Props = { children: React.ReactNode };

export const ProfileProvider: React.FC<Props> = ({ children }) => {
    const [user, setUser] = useState<PreCertUser | null>(null);
    const [isLoadingUser, setIsLoadingUser] = useState<boolean>(true);
    const auth = React.useContext(AuthContext);

    const hasLoadedRef = useRef(false);
    const cancelRef = useRef(false);

    const updateSelectedTrainingProgramId = (selectedTrainingProgramId: number) => {
        if (!user || !auth) return;

        const updated = deepCopy(user);
        updated.selectedTrainingProgramId = selectedTrainingProgramId;

        try {
            localStorage.setItem("trainingProgramId", String(selectedTrainingProgramId));
        } catch { }

        auth.currentUser = updated;
        setUser(updated);
    };

    const tryFetchProfile = useCallback(async (): Promise<PreCertUser> => {
        const maxAttempts = 3;
        const wait = (ms: number) => new Promise(r => setTimeout(r, ms));

        for (let i = 1; i <= maxAttempts; i++) {
            // IMPORTANT: do not continue profile loading if logout happened
            if (!auth?.isAuthenticated()) {
                throw new Error("LOGGED_OUT");
            }

            // Ensure token is ready after sign-in, but stop if no account/session
            const token = await auth.getAccessToken();
            if (!token && !auth?.getNativeSession?.() && !auth?.getOnBehalfOf?.()) {
                throw new Error("LOGGED_OUT");
            }

            try {
                return await abpnClient.get<PreCertUser>("/api/profile/getcurrentuser", auth);
            } catch (e: any) {
                // During logout / post-logout, do not turn this into errorPage
                if (!auth?.isAuthenticated()) {
                    throw new Error("LOGGED_OUT");
                }

                const msg = String(e?.message ?? "");
                const authish =
                    msg.includes("401") ||
                    msg.includes("Unauthorized") ||
                    msg.includes("403") ||
                    msg.includes("NetworkError");

                if (!authish || i === maxAttempts) throw e;
                await wait(250 * i);
            }
        }

        throw new Error("profile fetch failed");
    }, [auth]);

    const loadUser = useCallback(async () => {
        if (!auth) return;

        try {
            await auth.onReady?.();
        } catch {
            // ignore init race
        }

        // Hard stop: do not load profile on public/error routes or while logged out
        if (!auth.isAuthenticated() || IsErrorPage()) {
            auth.currentUser = null;
            setUser(null);
            setIsLoadingUser(false);
            hasLoadedRef.current = false;
            return;
        }

        setIsLoadingUser(true);

        try {
            const result = await tryFetchProfile();
            if (cancelRef.current) return;

            // Logout may have happened while request was in flight
            if (!auth.isAuthenticated()) {
                auth.currentUser = null;
                setUser(null);
                setIsLoadingUser(false);
                hasLoadedRef.current = false;
                return;
            }

            const tps = result.trainingPrograms ?? [];
            let saved: number | undefined;

            try {
                const raw = localStorage.getItem("trainingProgramId");
                saved = raw != null ? Number(raw) : undefined;
            } catch { }

            const valid =
                typeof saved === "number" &&
                !Number.isNaN(saved) &&
                tps.some(tp => tp.trainingProgramId === saved);

            if (!valid) {
                const firstId = tps[0]?.trainingProgramId;
                saved = firstId ?? (undefined as unknown as number);

                if (firstId != null) {
                    try {
                        localStorage.setItem("trainingProgramId", String(firstId));
                    } catch { }
                }
            }

            const withSel: PreCertUser = {
                ...result,
                selectedTrainingProgramId: saved,
            };

            auth.currentUser = withSel;
            setUser(withSel);
            setIsLoadingUser(false);
            hasLoadedRef.current = true;
        } catch (e: any) {
            const msg = String(e?.message ?? "");

            // THIS is the important logout fix:
            // logout / no-session / no-account should NOT go to error page
            if (
                msg === "LOGGED_OUT" ||
                !auth.isAuthenticated()
            ) {
                auth.currentUser = null;
                setUser(null);
                setIsLoadingUser(false);
                hasLoadedRef.current = false;
                return;
            }

            console.error("Failed to load profile", e?.message ?? e);
            auth.currentUser = null;
            setUser(null);
            setIsLoadingUser(false);
            hasLoadedRef.current = false;

            if (!IsErrorPage()) {
                redirectOnce(ERROR_PATH, "profileErrorRedirect");
            }
        }
    }, [auth, tryFetchProfile]);

    useEffect(() => {
        cancelRef.current = false;
        loadUser();

        return () => {
            cancelRef.current = true;
        };
    }, [loadUser]);

    useEffect(() => {
        if (!auth?.subscribe) return;

        const unsubscribe = auth.subscribe(() => {
            if (!auth.isAuthenticated()) {
                auth.currentUser = null;
                setUser(null);
                setIsLoadingUser(false);
                hasLoadedRef.current = false;
                return;
            }

            hasLoadedRef.current = false;
            loadUser();
        });

        return unsubscribe;
    }, [auth, loadUser]);

    return (
        <ProfileContext.Provider
            value={{ user, isLoadingUser, updateSelectedTrainingProgramId }}
        >
            {children}
        </ProfileContext.Provider>
    );
};

export default ProfileProvider;